import type { ReactNode } from 'react'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import Text from 'src/components/ts-components/Text'
import CopyComponent from 'src/components/CopyComponent'

const PLACEHOLDERS = {
  player: '/images/generic_placeholders/placeholder_player.png',
  team: '/images/generic_placeholders/placeholder_team.png',
} as const

const LABELS = {
  player: {
    caption: 'Player Name',
    idLabel: 'Player ID',
    fallbackName: 'View player',
  },
  team: {
    caption: 'Team Name',
    idLabel: 'Team ID',
    fallbackName: 'View team',
  },
} as const

type EntityKind = 'player' | 'team'

interface EntityProfileLinkProps {
  kind: EntityKind
  id: string
  name?: string | null
  image?: { full_path?: string; filename?: string } | string | null
  /** When true, name renders as plain text instead of a link (e.g. bucket teams). */
  disableLink?: boolean
  /** Rendered on the name's line, e.g. the waiver pill. */
  nameAdornment?: ReactNode
  /** Hides the ID chip when the page shows it elsewhere (the header). */
  hideId?: boolean
}

const EntityProfileLink = ({
  kind,
  id,
  name,
  image,
  disableLink = false,
  nameAdornment,
  hideId = false,
}: EntityProfileLinkProps) => {
  const { caption, idLabel, fallbackName } = LABELS[kind]
  const placeholder = PLACEHOLDERS[kind]
  const displayName = name || fallbackName

  const imageSrc = getImageThumbnailId({ image }) || placeholder
  const href =
    kind === 'player'
      ? `${ROUTES.PLAYER_ROOT}/${id}`
      : `${ROUTES.TEAM_ROOT}/${id}`

  const nameContent = (
    <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
      {displayName}
    </Text>
  )

  return (
    <Container>
      {hideId ? null : (
        <IdSlot>
          <CopyComponent
            customText={idLabel}
            id={id}
            alternativeBackground={false}
          />
        </IdSlot>
      )}
      <Avatar
        src={imageSrc}
        alt={displayName}
        width={80}
        height={80}
        isCircular={kind === 'player'}
        onError={(e) => {
          const img = e.target as HTMLImageElement
          img.onerror = null
          img.src = placeholder
        }}
      />
      <Info>
        <Text
          variant='captionXSmall'
          weight='semiBold'
          color={colors.NEUTRAL_50}
          // Same eyebrow as the profile's "Player Profile" label.
          style='letter-spacing: 1.44px;'
        >
          {caption}
        </Text>
        <NameRow>
          {disableLink ? (
            nameContent
          ) : (
            <NameLink href={href} target='_blank' rel='noopener noreferrer'>
              {nameContent}
            </NameLink>
          )}
          {nameAdornment ? (
            <NameAdornment>{nameAdornment}</NameAdornment>
          ) : null}
        </NameRow>
      </Info>
    </Container>
  )
}

export default EntityProfileLink

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: ${colors.SECONDARY_200};
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
`

const IdSlot = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`

const Avatar = styled.img<{ isCircular?: boolean }>`
  border-radius: ${({ isCircular }) => (isCircular ? '50%' : '8px')};
  object-fit: contain;
  background: ${colors.NEUTRAL_0};
  flex-shrink: 0;
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

// Name and adornment on one line; the adornment drops below on a narrow card.
const NameRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

// The condensed heading's caps sit low in their line box, so a pill centred on
// the box reads high; a small top margin nudges it down to the caps.
const NameAdornment = styled.div`
  margin-top: 2px;
`

const NameLink = styled.a`
  color: ${colors.WHITE};
  text-decoration: none;
  &:hover {
    color: ${colors.WHITE};
    text-decoration: underline;
  }
`
